import { useCallback, useEffect, useRef } from 'react';

import { isInDesignerMode } from '@lumapps/contents/ducks/selectors';
import { inputLanguageSelector } from '@lumapps/languages';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { FrontOfficeStore } from '@lumapps/redux/types';

import { getWidget } from '../../ducks/selectors';
import { actions } from '../../ducks/slice';
import { fetchWidgetBlocksByProperties } from '../../ducks/thunks/fetchWidgetBlocksByProperties';
import { WidgetState } from '../../ducks/type';
import { isSingleContainerBlock, LegacyWidget } from '../../types';

export const useLegacyWidget = ({
    legacyWidget,
    isWidgetEmpty,
    editingContent,
}: {
    legacyWidget: LegacyWidget;
    isWidgetEmpty?: () => boolean;
    editingContent?: boolean;
}): {
    refetch: () => void;
    fetchMore?: () => void;
    widget: WidgetState;
} => {
    const widgetId = legacyWidget.uuid;
    const dispatch = useDispatch();
    const inputLang = useSelector(inputLanguageSelector);
    const widget = useSelector((state: FrontOfficeStore) => getWidget(state, { widgetId }));
    const status = widget?.state;
    const isInDesigner = Boolean(useSelector(isInDesignerMode));

    const setWidget = useCallback((newWidget?: WidgetState) => dispatch(actions.setWidget(newWidget)), [dispatch]);
    const setStatus = useCallback(
        (newStatus: WidgetState['state']) =>
            dispatch(actions.setWidgetProperties({ widgetId, widgetProperties: { state: newStatus } })),
        [dispatch, widgetId],
    );

    /** In case of error during a fetch, we automatically retry this fetch for a maximum of 3 attempts. */
    const MAX_FETCH_RETRY = 3;
    const countRetry = useRef(0);

    useEffect(() => {
        if (
            widget &&
            !widget.body &&
            (!status || (status === 'error' && countRetry.current < MAX_FETCH_RETRY)) &&
            !editingContent
        ) {
            /**
             * Whether we fetch the widget or not is based on multiple factors:
             *     - The widget body is not set.
             *     - The state of the widget is either `undefined` (which means we haven't done anything yet) or
             *     in `'error'` (in which case we retry the fetch if we haven't already tried more than 3 times).
             * Also we prevent the fetch if the widget is being edited (useful for contribution widget).
             */
            countRetry.current += 1;
            dispatch(fetchWidgetBlocksByProperties({ legacyWidget, params: { forceDisplay: isInDesigner } }));
        }
    }, [dispatch, isInDesigner, legacyWidget, status, widget, widgetId, editingContent]);

    // Reset everything if the input properties changed, and the widget is not being edited (useful for contribution widget).
    useEffect(() => {
        if (!editingContent) {
            countRetry.current = 0;
            setWidget({ widgetId, widgetType: legacyWidget.widgetType });
            setStatus(undefined);
        }
    }, [legacyWidget, setStatus, setWidget, widgetId, inputLang, editingContent]);

    useEffect(() => {
        const isListEmpty = widget?.body?.type === 'BlockNoResults';
        const isAugmentedListEmpty =
            widget?.body &&
            isSingleContainerBlock(widget?.body) &&
            widget?.body?.container.type === 'BlockNoResults' &&
            status === 'loaded';
        const isSimpleWidgetEmpty = isWidgetEmpty && isWidgetEmpty();

        if (isListEmpty || isAugmentedListEmpty || isSimpleWidgetEmpty) {
            setStatus('empty');
        }
    }, [isWidgetEmpty, setStatus, status, widget, inputLang]);

    const fetchMore = useCallback(
        () =>
            dispatch(
                fetchWidgetBlocksByProperties({
                    params: { cursor: widget?.cursor, forceDisplay: isInDesigner },
                    legacyWidget,
                }),
            ),
        [dispatch, isInDesigner, legacyWidget, widget?.cursor],
    );

    return {
        refetch: fetchMore,
        fetchMore: widget?.more ? fetchMore : undefined,
        widget: { widgetType: legacyWidget.widgetType, widgetId: legacyWidget.uuid, ...(widget || {}), state: status },
    };
};
